import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Users, Building2, Trophy, Heart, TrendingUp, Briefcase, GraduationCap, History, FileText } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SocialSidebar from '../components/layout/SocialSidebar';
import CareersSection from '../components/about/CareersSection';
import { companyInfo } from '../data/mockData';

// About Us sidebar navigation items
const aboutSidebarItems = [
  { label: 'Profile', path: '/about/profile', icon: Building2 },
  { label: 'Overview', path: '/about/overview', icon: FileText },
  { label: 'History', path: '/about/history', icon: History },
  { label: 'Careers', path: '/about/careers', icon: Briefcase }
];

// Content for each section
const aboutContent = {
  'profile': {
    title: 'Profile',
    heroImage: '/about-bg.jpg',
    content: (
      <>
        {/* Company Profile Table */}
        <div className="mb-8 overflow-x-auto">
          <table className="w-full border-collapse">
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700 w-1/4">Legal Form</td>
                <td className="py-4 text-gray-600 leading-relaxed">
                  Incorporated as a Private Limited Liability Company in accordance with the Companies Act No. 07 of 2007, and registered as an authorized Financial Institution under the Finance Business Act No. 42 of 2011.
                </td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700">New Registration Number (Under the Companies Act No.07 of 2007)</td>
                <td className="py-4 text-gray-600">PV130285</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700">Registered Office</td>
                <td className="py-4 text-gray-600">11 Mile Post, Monaragala Road, Buttala.</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700">Telephone/Fax No</td>
                <td className="py-4 text-gray-600">+94 55 313 0227 / +94 70 601 0100</td>
              </tr>

              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700">E-mail</td>
                <td className="py-4 text-gray-600">
                  <a href="mailto:pridepathlk@gmail.com" className="text-[#2A9D8F] hover:underline">pridepathlk@gmail.com</a>
                </td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700">Website</td>
                <td className="py-4 text-gray-600">
                  <a href="https://www.pridepath.lk" target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] hover:underline">www.pridepath.lk</a>
                </td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700">Chairman</td>
                <td className="py-4 text-gray-600">Mr. Priyantha Rohana Bandara</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-4 pr-6 align-top font-semibold text-gray-700">General Manager</td>
                <td className="py-4 text-gray-600">Mr. Ranjith Wijelal</td>
              </tr>

            </tbody>
          </table>
        </div>

        {/* CEO Quote Section */}
        <div className="mt-12 relative bg-gradient-to-r from-gray-50 to-white rounded-xl overflow-hidden">
          <div className="flex flex-col md:flex-row items-center">
            {/* Quote Content */}
            <div className="flex-1 p-8 md:p-12">
              {/* Large Quote Mark */}
              <div className="text-8xl text-[#2A9D8F]/20 font-serif leading-none mb-4">"</div>

              <p className="text-gray-600 leading-relaxed text-justify mb-8" style={{ textAlign: 'justify' }}>
                Our role in society is to be an enabler. It's a responsibility we are passionate about and our purpose: EMPOWERING ASPIRATIONS, underlines that. It's something our entire team is committed to and work hard every day to make possible. We firmly believe in financial inclusion, making luxury more accessible and bringing people closer to the things or experiences they seek. This belief is what drives us to create more technology-based products and services that enable people to achieve their aspirations.
              </p>

              {/* CEO Info */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Mr. Priyantha Rohana Bandara</h3>
                <p className="text-gray-500 text-sm mt-1">Managing Director & CEO</p>
              </div>
            </div>

            {/* CEO Image */}
            <div className="md:w-1/2 relative">
              <img
                src="/ceo.jpg"
                alt="Mr. Priyantha Rohana Bandara - Managing Director & CEO"
                className="w-full h-[400px] md:h-[500px] object-cover object-top"
              />
              {/* Gradient overlay for smooth blend */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-transparent to-transparent md:block hidden" />
            </div>
          </div>
        </div>

        {/* Board of Directors Section */}
        <div className="mt-16 relative">
          {/* Decorative Wave Background */}
          <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
            <svg className="absolute w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="none">
              <path d="M0,100 C300,200 400,0 600,100 C800,200 900,0 1200,100 L1200,600 L0,600 Z" fill="#2A9D8F" opacity="0.3" />
              <path d="M0,150 C200,250 500,50 700,150 C900,250 1000,50 1200,150 L1200,600 L0,600 Z" fill="#2A9D8F" opacity="0.2" />
            </svg>
          </div>

          {/* Section Title */}
          <h2 className="text-4xl md:text-5xl text-center text-gray-800 mb-12 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            Board of Directors
          </h2>

          {/* Directors List */}
          <div className="space-y-8 relative z-10">
            {[
              {
                name: 'Mr. Kaveesha Dilshan',
                role: 'Director',
                image: '/director-kaveesha.jpg',
                bio: 'A result-oriented professional with over 2+ years of experience in the financial services sector. Currently serving as a Director at Pridepath Financial, I specialize in strategic planning, risk management, and driving sustainable business growth. My focus is on empowering communities through inclusive financial solutions and enhancing organizational excellence through innovative leadership.'
              },
              {
                name: 'Mr. Ranjith Wijelal',
                role: 'General Manager',
                image: '/general-manager.jpg',
                bio: 'A visionary leader responsible for the overall strategic direction and operational excellence of Pride Path Commercial. Committed to driving sustainable growth and fostering a culture of integrity and customer satisfaction.'
              }
            ].map((director, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="flex flex-col md:flex-row">
                  {/* Director Photo */}
                  <div className="md:w-64 flex-shrink-0">
                    <img
                      src={director.image}
                      alt={director.name}
                      className="w-full h-64 md:h-full object-cover"
                    />
                  </div>

                  {/* Director Info */}
                  <div className="flex-1 p-6 md:p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{director.name}</h3>
                    <p className="text-gray-500 text-sm mb-4">{director.role}</p>
                    <p className="text-gray-600 leading-relaxed text-justify">{director.bio}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    )
  },
  'overview': {
    title: 'Overview',
    heroImage: '/overview-bg.png',
    content: (
      <>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">A Legacy of Growth Since 2002</h3>

        <p className="text-gray-600 leading-relaxed mb-6">
          The journey of the Manju Group of Companies began in 2002 with a singular mission: to deliver value and quality service to our stakeholders. What started as a modest venture has rapidly evolved into a dynamic group of companies, characterized by resilience and an ability to adapt to the changing economic landscape.
        </p>

        <p className="text-gray-600 leading-relaxed mb-6">
          As part of our strategic diversification, we identified a critical need for accessible and transparent financial services. This led to the formation of Pride Path Commercial (Pvt) Ltd, a fully registered financial institution incorporated in 2019.
        </p>

        <p className="text-gray-600 leading-relaxed mb-8">
          Today, Pride Path Commercial stands as a pillar of strength within the Manju Group. We combine the stability of our two-decade history with modern financial practices to offer services that help our customers navigate their path to success with confidence and pride.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            { value: '6+', label: 'Years of Legacy', delay: '0ms' },
            { value: '2019', label: 'Year Established', delay: '150ms' },
            { value: '100%', label: 'Customer Focused', delay: '300ms' }
          ].map((stat, idx) => (
            <div
              key={idx}
              className="stat-box bg-[#2A9D8F] text-white p-6 rounded-lg text-center cursor-pointer"
              style={{
                animation: `fadeInLeft 600ms ease-out ${stat.delay} both`,
                transition: 'transform 300ms ease, box-shadow 300ms ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(42, 157, 143, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div className="text-4xl font-bold mb-2">{stat.value}</div>
              <div className="text-white/80">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Animation Keyframes */}
        <style>{`
          @keyframes fadeInLeft {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes shadowPulse {
            0%, 100% {
              box-shadow: 0 10px 30px rgba(42, 157, 143, 0.3);
            }
            50% {
              box-shadow: 0 20px 40px rgba(42, 157, 143, 0.5);
            }
          }
          
          .stat-box:hover {
            animation: shadowPulse 1s ease-in-out infinite;
          }
        `}</style>
      </>
    )
  },
  'history': {
    title: 'History',
    heroImage: '/history-bg.png',
    content: (
      <>
        <p className="text-lg leading-relaxed mb-6">
          Our journey began with a vision to build a sustainable business empire. From our humble beginnings in 2002, we have evolved into a diversified group, now dedicated to empowering Sri Lankans through trusted and accessible financial solutions.
        </p>
        <div className="space-y-6">
          <div className="flex gap-4">
            <div className="w-24 flex-shrink-0 text-[#2A9D8F] font-bold">2002</div>
            <div className="border-l-2 border-[#2A9D8F] pl-4 pb-6">
              <h4 className="font-semibold text-gray-900">Foundation</h4>
              <p className="text-gray-600">Established the Manju Group of Companies with a commitment to excellence and a vision to serve diverse market needs in Sri Lanka.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-24 flex-shrink-0 text-[#2A9D8F] font-bold">2019</div>
            <div className="border-l-2 border-[#2A9D8F] pl-4 pb-6">
              <h4 className="font-semibold text-gray-900">New Horizons</h4>
              <p className="text-gray-600">Incorporated Pride Path Commercial (Pvt) Ltd as a registered financial institution, marking a strategic entry into the financial services sector.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-24 flex-shrink-0 text-[#2A9D8F] font-bold">2025</div>
            <div className="border-l-2 border-[#2A9D8F] pl-4">
              <h4 className="font-semibold text-gray-900">Rapid Expansion</h4>
              <p className="text-gray-600">Currently expanding our footprint rapidly, offering robust financial solutions and building trust as a leading financial partner in the region.</p>
            </div>
          </div>
        </div>
      </>
    )
  },

  'board-of-directors': {
    title: 'Board of Directors',
    heroImage: '/about-bg.png',
    content: (
      <>
        {/* Decorative Wave Background */}
        <div className="relative">
          {/* Wave Pattern SVG */}
          <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
            <svg className="absolute w-full h-full" viewBox="0 0 1200 400" preserveAspectRatio="none">
              <path d="M0,100 C300,200 400,0 600,100 C800,200 900,0 1200,100 L1200,400 L0,400 Z" fill="#2A9D8F" opacity="0.3" />
              <path d="M0,150 C200,250 500,50 700,150 C900,250 1000,50 1200,150 L1200,400 L0,400 Z" fill="#2A9D8F" opacity="0.2" />
            </svg>
          </div>

          {/* Section Title */}
          <h2 className="text-4xl md:text-5xl text-center text-gray-800 mb-12 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            Board of Directors
          </h2>

          {/* Directors List */}
          <div className="space-y-8">
            {[
              {
                name: 'Mr. Kaveesha Dilshan',
                role: 'Director',
                image: '/director-kaveesha.jpg',
                bio: 'A result-oriented professional with over 2+ years of experience in the financial services sector. Currently serving as a Director at Pridepath Financial, I specialize in strategic planning, risk management, and driving sustainable business growth. My focus is on empowering communities through inclusive financial solutions and enhancing organizational excellence through innovative leadership.'
              },
              {
                name: 'Mr. Ranjith Wijelal',
                role: 'General Manager',
                image: '/general-manager.jpg',
                bio: 'A visionary leader responsible for the overall strategic direction and operational excellence of Pride Path Commercial. Committed to driving sustainable growth and fostering a culture of integrity and customer satisfaction.'
              },
              {
                name: 'Mr. G.B. Epadage',
                role: 'Chairman / Non-Executive Director',
                image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=350&fit=crop&q=90',
                bio: 'A visionary leader with over 25 years of experience in the financial services industry. He has been instrumental in shaping the strategic direction of the company and has led numerous successful initiatives to expand the company\'s market presence across Sri Lanka.'
              },
              {
                name: 'Mr. R.S Epadage',
                role: 'Executive Director / General Manager',
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=350&fit=crop&q=90',
                bio: 'An accomplished executive with extensive expertise in operations and business development. He oversees the day-to-day operations of the company and has been pivotal in implementing innovative financial products and services that cater to the evolving needs of customers.'
              }
            ].map((director, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="flex flex-col md:flex-row">
                  {/* Director Photo */}
                  <div className="md:w-64 flex-shrink-0">
                    <img
                      src={director.image}
                      alt={director.name}
                      className="w-full h-64 md:h-full object-cover"
                    />
                  </div>

                  {/* Director Info */}
                  <div className="flex-1 p-6 md:p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{director.name}</h3>
                    <p className="text-gray-500 text-sm mb-4">{director.role}</p>
                    <p className="text-gray-600 leading-relaxed text-justify">{director.bio}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    )
  },

  'careers': {
    title: 'Careers',
    heroImage: '/careers-bg.png',
    content: <CareersSection />
  }
};

// Default to 'who-we-are' for backwards compatibility
aboutContent['who-we-are'] = aboutContent['profile'];

const AboutPage = () => {
  const { section } = useParams();
  const currentSection = section || 'profile';
  const pageData = aboutContent[currentSection] || aboutContent['profile'];
  const [hoveredItem, setHoveredItem] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{pageData.title} - About Us | {companyInfo.name}</title>
        <meta name="description" content={`${pageData.title} - Learn about ${companyInfo.name}`} />
      </Helmet>

      <Header />
      <SocialSidebar />

      <main>
        {/* Hero Banner - Same size as other pages */}
        <div className="relative h-[100svh] min-h-[550px] md:min-h-[650px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center brightness-125 contrast-105"
            style={{ backgroundImage: `url(${pageData.heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/15 via-transparent to-black/10" />
          <div className="absolute inset-0 flex flex-col justify-center pt-20">
            <div className="max-w-7xl mx-auto px-4 w-full">
              {/* Breadcrumb */}
              <nav className="flex items-center text-sm text-white/70 mb-4">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <ChevronRight size={16} className="mx-2" />
                <Link to="/about/profile" className="hover:text-white transition-colors">About</Link>
                <ChevronRight size={16} className="mx-2" />
                <span className="text-white">{pageData.title}</span>
              </nav>
              {/* Title - Larger Font */}
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.7)' }}>{pageData.title}</h1>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-8">

            {/* Sidebar Navigation */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden sticky top-[100px]">
                {/* Sidebar Header */}
                <div className="bg-gradient-to-r from-[#2A9D8F] to-[#004a8b] text-white px-5 py-4">
                  <h3 className="font-bold text-lg">About Us</h3>
                </div>

                {/* Navigation Items */}
                <nav className="py-2">
                  {aboutSidebarItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = item.path.includes(currentSection);
                    const isHovered = hoveredItem === index;

                    return (
                      <Link
                        key={index}
                        to={item.path}
                        onMouseEnter={() => setHoveredItem(index)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className={`flex items-center gap-3 px-5 py-3.5 text-sm transition-all duration-300 relative group ${isActive
                          ? 'bg-[#2A9D8F]/10 text-[#2A9D8F] border-l-4 border-[#2A9D8F] font-semibold'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-[#2A9D8F] border-l-4 border-transparent'
                          }`}
                        style={{
                          transform: isHovered && !isActive ? 'translateX(4px)' : 'translateX(0)',
                        }}
                      >
                        <Icon
                          size={18}
                          className={`flex-shrink-0 transition-colors ${isActive ? 'text-[#2A9D8F]' : 'text-gray-400 group-hover:text-[#2A9D8F]'}`}
                        />
                        <span className="flex-1">{item.label}</span>
                        <ChevronRight
                          size={16}
                          className={`transition-all ${isActive || isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'} ${isActive ? 'text-[#2A9D8F]' : 'text-gray-400'}`}
                        />
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-xl shadow-lg p-6 md:p-10">
                {/* Section Title */}
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {pageData.title}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#2A9D8F] to-[#004a8b] rounded-full mb-8" />

                {/* Section Content */}
                <div className="text-gray-600">
                  {pageData.content}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
